import { Check, CircleDollarSign, X } from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { PencilEditIcon } from '@/components/icons/pencil-edit';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tag } from '@/components/ui/tag';
import { Typography } from '@/components/ui/typography';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';

import type { BudgetRecommendationForm } from '../types';

interface BudgetEditProps {
  duration: number;
  hookForm: BudgetRecommendationForm;
}

export function BudgetEdit({ duration, hookForm }: BudgetEditProps) {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const [isEditing, setIsEditing] = useState(false);

  const {
    register,
    watch,
    formState: { errors },
    trigger,
  } = hookForm;
  const budget = watch('budget');
  const budgetError = errors?.budget?.message;

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleSaveClick = async () => {
    const isValid = await trigger('budget');
    if (!isValid) return;

    setIsEditing(false);
  };

  const handleCloseClick = () => {
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        {/* Budget input */}
        <Input
          isNumber
          {...register('budget', {
            setValueAs: value =>
              value === '' || value === null ? undefined : Number(value),
          })}
          placeholderIcon={
            <CircleDollarSign className="text-fx-neutral-400 size-4" />
          }
          placeholder={intl.formatMessage({
            defaultMessage: 'Enter budget',
            description:
              'Placeholder inside budget amount input while editing a recommendation',
          })}
          error={budgetError}
          className="!w-32"
        />

        {/* Check and Close buttons */}
        <Button
          type="button"
          variant="icon"
          className="bg-fx-green hover:bg-fx-green/80 size-6 rounded-sm"
          onClick={() => void handleSaveClick()}
        >
          <Check className="size-3 text-white" />
        </Button>
        <Button
          type="button"
          variant="icon"
          className="bg-fx-rose hover:bg-fx-rose/80 size-6 rounded-sm"
          onClick={handleCloseClick}
        >
          <X className="size-3 text-white" />
        </Button>
      </div>
    );
  }

  return (
    <>
      {/* Recommended budget and duration */}
      <div className="flex flex-col items-start">
        <Tag
          text={
            <>
              <span className="font-bold">
                {budget != null ? formatCurrency(budget) : '-'}
              </span>{' '}
              /{' '}
              <FormattedMessage
                defaultMessage="{duration, plural, one {{duration} day} other {{duration} days}}"
                description="Duration label next to currency in the recommended-budget tag"
                values={{ duration }}
              />
            </>
          }
          className="bg-fx-neutral-100"
        />
        {!!budgetError && !isEditing && (
          <Typography variant="small1" className="!text-fx-rose">
            {budgetError}
          </Typography>
        )}
      </div>

      {/* Edit button */}
      <Button
        type="button"
        variant="icon"
        size="icon"
        onClick={() => void handleEditClick()}
      >
        <PencilEditIcon className="text-fx-neutral-600" />
      </Button>
    </>
  );
}
